/**
 * Composer — modern agentic input box.
 *
 * Visual model: an AI-chat surface (Claude.ai / Anthropic Console / ChatGPT)
 * lifted with glass blur + acid-lime focus glow. Three controls: prompt
 * textarea, reference chips, aspect ratio pill, primary "Direct →" CTA.
 *
 * Behaviour is unchanged: POST /projects then window.location.href to the
 * canvas console workspace with ?initialPrompt=<encoded>.
 */
import { useState, useRef, useEffect, useMemo, FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { api, type AspectRatio, type CreateProjectRequest, type ProductionTarget } from '@ponpon/api-client'
import { useAuth } from '../contexts/AuthContext'
import { rejectionReason, uploadKind, type ComposerUpload } from '../lib/uploads'
import { requiredUploadKind, type TemplateSourceKind } from '../templates/registry'

/** An attachment that made it to the server, handed to the workspace. */
export type UploadedRef = ComposerUpload

/** sessionStorage key for the "sign in then resume" handoff.
 *
 * Set when an unauthenticated user submits the Composer; read on
 * remount after auth completes so the project creation resumes
 * automatically (user types prompt → clicks Enter → sees login →
 * signs in → lands in workspace with their project already created).
 *
 * Stored value shape: ``{prompt: string, aspectRatio: AspectRatio,
 * stashedAt: number}``. ``stashedAt`` lets us TTL stale stashes (a
 * user who closed the tab a week ago shouldn't get auto-replayed
 * on next visit — they probably forgot what they typed).
 */
const PENDING_COMPOSE_KEY = 'ponpon:pending_compose'
const PENDING_COMPOSE_TTL_MS = 30 * 60 * 1000 // 30 minutes

interface PendingCompose {
  prompt: string
  aspectRatio: AspectRatio
  // Optional for backward compat with stashes written before the
  // target selector existed; readers default to 'video'.
  target?: ProductionTarget
  // Optional for the same reason. A source-material recipe cannot be
  // auto-resumed: the attachments do not survive the sign-in detour.
  sourceKind?: TemplateSourceKind
  stashedAt: number
}

function readPendingCompose(): PendingCompose | null {
  try {
    const raw = sessionStorage.getItem(PENDING_COMPOSE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PendingCompose
    if (!parsed.prompt || !parsed.aspectRatio || !parsed.stashedAt) return null
    if (Date.now() - parsed.stashedAt > PENDING_COMPOSE_TTL_MS) {
      sessionStorage.removeItem(PENDING_COMPOSE_KEY)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function clearPendingCompose() {
  try {
    sessionStorage.removeItem(PENDING_COMPOSE_KEY)
  } catch {
    /* sessionStorage may be unavailable in private-browse mode */
  }
}

interface TemplateSeed {
  prompt: string
  aspectRatio: AspectRatio
  templateId?: string
  sourceKind?: TemplateSourceKind
}

/** What the composer asks for when a recipe needs the user's own material. */
const REQUIRED_UPLOAD_HINT: Record<'image' | 'video', string> = {
  image: 'This recipe animates your photo — attach one to run it.',
  video: 'This recipe edits your footage — attach a video to run it.',
}

const ASPECT_RATIOS: { value: AspectRatio; label: string }[] = [
  { value: '16:9', label: '16:9' },
  { value: '9:16', label: '9:16' },
]

/**
 * Output medium, not visual style. "Anime" is a look you ask for in the
 * prompt and can apply to any of these; these change what artifact you get.
 *
 * `music_video` was already a first-class `production_target` on the backend
 * — the API accepts it, and `mv_enabled_for()` switches the Director onto the
 * MV tool set (and hides the drama/webtoon recipe tools) whenever a project
 * carries it. It simply had no tab, so the whole music-video pipeline was
 * unreachable from the UI.
 *
 * `webtoon` reads as a style to people who don't know the format; it produces
 * a vertical-scroll comic — still panels with lettering, no video at all. The
 * label says Comic for that reason. The stored value stays `webtoon`: it is
 * baked into two node schemas, the YDoc wire format and existing
 * `workspace_state` rows, and renaming it would buy nothing a label cannot.
 */
const TARGETS: { value: ProductionTarget; label: string; hint: string }[] = [
  { value: 'video', label: 'Video', hint: 'Scenes, cast and camera — cut and scored' },
  { value: 'music_video', label: 'Music video', hint: 'A track plus cuts timed to it' },
  { value: 'webtoon', label: 'Comic', hint: 'Vertical-scroll webtoon — panels with dialogue, no video' },
  { value: 'ads', label: 'Ad', hint: 'Short paid creative with a hook' },
]

const MAX_REFS = 5

export function Composer() {
  const navigate = useNavigate()
  const location = useLocation()
  const { session } = useAuth()
  const seed = (location.state as { templateSeed?: TemplateSeed } | null)?.templateSeed
  // Pre-fill from pending compose if the user is returning from a
  // sign-in detour. ``readPendingCompose`` enforces a 30-min TTL so a
  // stale stash from a previous session doesn't auto-fire. Memoised
  // so the auto-resume effect below has a stable identity to depend
  // on across renders (a fresh ``readPendingCompose()`` per render
  // wouldn't cause double-submits — guarded — but stable is cleaner).
  const pending = useMemo(() => readPendingCompose(), [])
  const [prompt, setPrompt] = useState(seed?.prompt ?? pending?.prompt ?? '')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>(
    seed?.aspectRatio ?? pending?.aspectRatio ?? '16:9',
  )
  const [target, setTarget] = useState<ProductionTarget>(pending?.target ?? 'video')
  // Where the seeded recipe's raw material comes from. Kept apart from
  // `seededFromTemplate`: editing the prompt (say, adding the pet's name)
  // hides the "seeded" badge but still needs the photo.
  const [sourceKind, setSourceKind] = useState<TemplateSourceKind>(
    seed?.sourceKind ?? pending?.sourceKind ?? 'prompt',
  )
  const [refs, setRefs] = useState<File[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [seededFromTemplate, setSeededFromTemplate] = useState<string | null>(
    seed?.templateId ?? null,
  )
  const [focused, setFocused] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // The mutation reads the attachments through a ref rather than closing over
  // `refs`: react-query captures `mutationFn` when the mutation is created,
  // so a closure would see whatever the array was at that moment, not at
  // submit time.
  const filesRef = useRef<File[]>([])
  useEffect(() => {
    filesRef.current = refs
  }, [refs])

  useEffect(() => {
    if (seed) window.history.replaceState({}, '', location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Late seeds: the landing's chips/template rail navigate('/') with a new
  // templateSeed while the Composer is already mounted — the useState
  // initializers above only catch a seed present at mount. Apply any seed
  // object we haven't applied yet.
  const lastSeedRef = useRef(seed)
  useEffect(() => {
    if (seed && seed !== lastSeedRef.current) {
      lastSeedRef.current = seed
      setPrompt(seed.prompt)
      setAspectRatio(seed.aspectRatio)
      setSeededFromTemplate(seed.templateId ?? null)
      setSourceKind(seed.sourceKind ?? 'prompt')
      textareaRef.current?.focus()
    }
  }, [seed])

  // Auto-grow the textarea
  useEffect(() => {
    const t = textareaRef.current
    if (!t) return
    t.style.height = 'auto'
    t.style.height = Math.min(t.scrollHeight, 260) + 'px'
  }, [prompt])

  const create = useMutation({
    mutationFn: async (req: CreateProjectRequest) => {
      const project = await api.createProject(req)

      // Upload the attachments. They used to be dropped on the floor here:
      // the user picked files, saw them as chips with a "1/5" counter, got
      // an "@Video 1" token written into their prompt — and nothing was ever
      // sent. Confirmed against production by capturing the network: zero
      // upload requests. The Director then received a prompt citing
      // "@Video 1" and correctly replied that no such clip was on the
      // canvas, which is a confusing way to learn your upload silently
      // failed.
      //
      // Uploads happen after createProject because the endpoint is
      // project-scoped, and sequentially rather than in parallel: these are
      // media files near the 31 MB ceiling, and five concurrent multipart
      // POSTs from a phone connection is how you get timeouts.
      const uploaded: UploadedRef[] = []
      for (const file of filesRef.current) {
        const path = await api
          .uploadAsset(project.id, file)
          .then((r) => r.path)
          .catch(() => null)
        if (path) uploaded.push({ name: file.name, path, kind: uploadKind(file) })
      }
      return { project, prompt: req.prompt, uploaded }
    },
    onSuccess: ({ project, prompt, uploaded }) => {
      // Land in v2's own workspace. The chat panel auto-sends the
      // initial prompt via location.state (no cross-origin hop).
      // Also clear any pending-compose stash — we just consumed it.
      clearPendingCompose()
      navigate(`/project/${project.id}/workspace`, {
        state: { initialPrompt: prompt, uploadedRefs: uploaded },
      })
    },
    onError: () => {
      // Clear the stash on failure too — otherwise the next mount
      // auto-resumes the SAME failed request indefinitely (e.g. a
      // 5xx + page refresh would loop). The user can re-submit
      // manually from the still-pre-filled textarea.
      clearPendingCompose()
    },
  })

  // Auto-resume after a sign-in detour: if the user typed a prompt,
  // hit Enter while logged out, signed in, and was bounced back to
  // ``/``, this effect fires once on the first render with both a
  // live session AND a pending stash — kicking off createProject the
  // same way as if they'd clicked Enter while authed.
  //
  // ``create.isPending`` / ``create.isSuccess`` guards prevent a
  // duplicate submit if the effect re-runs (e.g. session refresh).
  useEffect(() => {
    if (!session || !pending) return
    if (create.isPending || create.isSuccess) return
    // Attachments cannot ride through sessionStorage, so a recipe that needs
    // one is left pre-filled for the user to attach and send — auto-firing it
    // would run a "use my photo" prompt with no photo.
    if (requiredUploadKind(pending.sourceKind)) return
    create.mutate({
      prompt: pending.prompt,
      aspect_ratio: pending.aspectRatio,
      production_target: pending.target ?? 'video',
    })
    // pending is captured at mount; we intentionally don't re-run on
    // session changes that happen later.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  function addFiles(incoming: FileList | File[]) {
    // Reject at pick time, not at upload time. Cloud Run answers a body over
    // 32 MiB with a plain-HTML 413 the app never sees; the browser then
    // retries silently. Telling the user here — while they still have the
    // file picker in mind — is the only point where the message is useful.
    const candidates = Array.from(incoming).slice(0, MAX_REFS - refs.length)
    const accepted: File[] = []
    const problems: string[] = []
    for (const file of candidates) {
      const reason = rejectionReason(file)
      if (reason) problems.push(reason)
      else accepted.push(file)
    }
    setUploadError(problems.length ? problems[0] : null)
    if (accepted.length) setRefs((cur) => [...cur, ...accepted].slice(0, MAX_REFS))
  }
  function removeRef(idx: number) {
    setRefs((cur) => cur.filter((_, i) => i !== idx))
  }
  function refToken(idx: number, file: File): string {
    return file.type.startsWith('video/') ? `@Video ${idx + 1}` : `@Image ${idx + 1}`
  }
  function appendToken(tok: string) {
    setPrompt((cur) => (cur ? `${cur} ${tok}` : tok))
  }

  // A footage/photo recipe describes a treatment of the user's material; run
  // without it, the Director would invent a subject instead.
  const requiredKind = requiredUploadKind(sourceKind)
  const missingUpload = requiredKind !== null && !refs.some((f) => uploadKind(f) === requiredKind)

  async function onSubmit(e?: FormEvent) {
    e?.preventDefault()
    if (!prompt.trim() || create.isPending || missingUpload) return
    // Unauthed branch: stash the in-flight prompt + aspect ratio and
    // redirect to /login. The auto-resume effect above will pick the
    // stash back up after Firebase populates ``session`` and call
    // createProject for real. The user experience reads as "type prompt
    // → sign in → land in workspace" without the user having to
    // re-paste anything.
    //
    // Reference files (``refs``) are NOT stashed — File objects can't
    // round-trip through sessionStorage. The user has to re-upload
    // after sign-in if they had references attached. Acceptable
    // trade-off for the v1 of this flow; if user feedback says
    // otherwise, upload to a pre-auth signed URL instead.
    if (!session) {
      try {
        const payload: PendingCompose = {
          prompt: prompt.trim(),
          aspectRatio,
          target,
          sourceKind,
          stashedAt: Date.now(),
        }
        sessionStorage.setItem(PENDING_COMPOSE_KEY, JSON.stringify(payload))
      } catch {
        /* Private-browse mode without sessionStorage: fall through to
           login. User will lose the prompt — acceptable degradation. */
      }
      navigate('/login')
      return
    }
    create.mutate({
      prompt: prompt.trim(),
      aspect_ratio: aspectRatio,
      production_target: target,
    })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // Enter submits; Shift+Enter inserts a newline. Skip while an IME
    // composition (e.g. Pinyin) is using Enter to commit candidates.
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      onSubmit()
    }
  }

  const disabled = !prompt.trim() || create.isPending || missingUpload

  return (
    <form
      onSubmit={onSubmit}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files)
      }}
      className="relative w-full max-w-3xl mx-auto"
    >
      {/* Subtle accent halo behind */}
      <div className="absolute -inset-6 -z-10 pointer-events-none">
        <div className="absolute inset-x-12 top-4 h-px accent-strip" />
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full opacity-50"
             style={{ background: 'radial-gradient(circle, rgba(212,255,58,0.18), transparent 60%)' }} />
      </div>

      <div
        className={`glass rounded-2xl transition-[background-color,border-color,box-shadow] duration-300 ${
          focused ? 'shadow-accent' : 'shadow-md'
        } ${dragOver ? 'ring-2 ring-accent ring-offset-0' : ''}`}
        style={{
          borderRadius: 'var(--r-lg)',
        }}
      >
        {/* Seeded from template badge */}
        {seededFromTemplate && (
          <div className="px-5 pt-4 flex items-center justify-between gap-2">
            <div className="pill pill--accent">
              <span>seeded · {seededFromTemplate}</span>
            </div>
            <button
              type="button"
              onClick={() => { setPrompt(''); setSeededFromTemplate(null); setSourceKind('prompt') }}
              className="btn-quiet"
            >
              Clear
            </button>
          </div>
        )}

        {/* Textarea */}
        <div className="px-5 sm:px-6 pt-5">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => {
              setPrompt(e.target.value)
              if (seededFromTemplate) setSeededFromTemplate(null)
            }}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            placeholder="A neon-drenched street race in 2089. Two riders. Eight beats. End on the third overtake…"
            rows={3}
            className="w-full resize-none bg-transparent text-text placeholder:text-text-3 text-[1.12rem] leading-relaxed focus:outline-none"
            style={{ minHeight: '6.6rem' }}
          />
        </div>

        {/* A rejected file has to say so here. Silently not-attaching is how
            the previous version behaved, and the user only discovered it when
            the Director said their clip wasn't on the canvas. */}
        {uploadError && (
          <div
            role="alert"
            data-testid="upload-error"
            className="px-5 sm:px-6 pb-2 text-[0.8rem] text-[#f0567a]"
          >
            {uploadError}
          </div>
        )}

        {missingUpload && requiredKind && (
          <div
            data-testid="required-upload-hint"
            className="px-5 sm:px-6 pb-2 flex items-center gap-2 text-[0.8rem] text-text-2"
          >
            <span>{REQUIRED_UPLOAD_HINT[requiredKind]}</span>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="btn-quiet"
            >
              <PaperclipIcon />
              <span>Attach</span>
            </button>
          </div>
        )}

        {/* Reference chips row (only when refs exist) */}
        {refs.length > 0 && (
          <div className="px-5 sm:px-6 pb-2 flex flex-wrap gap-1.5">
            {refs.map((f, i) => (
              <div
                key={i}
                className="group inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-md bg-white/[0.04] border border-line-2 text-[0.78rem]"
              >
                <button
                  type="button"
                  onClick={() => appendToken(refToken(i, f))}
                  className="t-mono text-accent text-[0.72rem] tracking-wide hover:underline"
                  title="Insert into prompt"
                >
                  {refToken(i, f)}
                </button>
                <span className="text-text-3 truncate max-w-[10rem]">{f.name}</span>
                <button
                  type="button"
                  onClick={() => removeRef(i)}
                  aria-label="Remove"
                  className="w-5 h-5 inline-flex items-center justify-center rounded text-text-3 hover:text-err hover:bg-white/[0.06]"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Bottom action bar */}
        <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-line">
          {/* Left controls — attach, aspect */}
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) addFiles(e.target.files)
                e.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={refs.length >= MAX_REFS}
              className="btn-quiet"
              title={`Attach (${refs.length}/${MAX_REFS})`}
            >
              <PaperclipIcon />
              <span className="hidden sm:inline">Attach</span>
              {refs.length > 0 && <span className="t-mono text-[0.72rem] text-text-3">{refs.length}/{MAX_REFS}</span>}
            </button>

            <div className="hidden sm:flex items-center gap-1 ml-1">
              {ASPECT_RATIOS.map(opt => {
                const active = aspectRatio === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setAspectRatio(opt.value)}
                    className={`t-mono text-[0.78rem] px-2.5 py-1.5 rounded-md transition-colors ${
                      active ? 'bg-accent/15 text-accent border border-accent/30' : 'text-text-3 hover:text-text border border-transparent'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>

            <div className="hidden sm:flex items-center gap-1 ml-1" data-testid="target-toggle">
              {TARGETS.map(opt => {
                const active = target === opt.value
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTarget(opt.value)}
                    // The labels are terse enough to be ambiguous on their
                    // own — "Comic" especially, since the tab sits in a row
                    // of video formats.
                    title={opt.hint}
                    aria-pressed={active}
                    className={`t-mono text-[0.78rem] px-2.5 py-1.5 rounded-md transition-colors ${
                      active ? 'bg-accent/15 text-accent border border-accent/30' : 'text-text-3 hover:text-text border border-transparent'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Right — model pill + send */}
          <div className="flex items-center gap-2">
            <div className="hidden sm:inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1.5 rounded-md bg-white/[0.03] border border-line-2">
              <span className="live-dot" />
              <span className="t-mono text-[0.74rem] text-text-2">haevi · Director v2</span>
            </div>
            <button type="submit" disabled={disabled} className="btn-accent" style={{ padding: '0.6rem 1.05rem' }}>
              {create.isPending ? (
                <>
                  <Spinner />
                  <span>Directing…</span>
                </>
              ) : (
                <>
                  <span>Direct</span>
                  <ArrowIcon />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Helper line */}
      <div className="mt-3 px-1 flex items-center justify-between text-[0.78rem] text-text-3">
        <span>
          ⌘ + ↵ to send · Use <span className="t-mono text-text-2">@Image 1</span> to cite uploads
        </span>
        {create.isError && (
          <span className="text-err">
            ⨯ {(create.error as Error).message || 'Failed to create project'}
          </span>
        )}
      </div>
    </form>
  )
}

function PaperclipIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21.44 11.05L12.25 20.24a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  )
}

function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" className="animate-spin">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 00-9-9" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}
